import { ChangeDetectionStrategy, Component, inject, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Router } from "@angular/router";

import { PendingOrganizationInvitationResponse } from "@bitwarden/common/admin-console/models/response/pending-organization-invitation.response";
import { I18nService } from "@bitwarden/common/platform/abstractions/i18n.service";
import { SyncService } from "@bitwarden/common/vault/abstractions/sync/sync.service.abstraction";
import { ButtonModule, MenuModule, ToastService } from "@bitwarden/components";
import { I18nPipe } from "@bitwarden/ui-common";
import { VaultOrganizationUserNotificationsService } from "@bitwarden/vault";

import { SharedModule } from "../../shared";

export type PendingOrganizationInvitation = PendingOrganizationInvitationResponse;

@Component({
  selector: "app-notification-bell",
  templateUrl: "./notification-bell.component.html",
  imports: [SharedModule, MenuModule, ButtonModule, I18nPipe],
  providers: [VaultOrganizationUserNotificationsService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationBellComponent {
  private readonly vaultOrganizationUserNotificationsService = inject(
    VaultOrganizationUserNotificationsService,
  );
  private readonly router = inject(Router);
  private readonly i18nService = inject(I18nService);
  private readonly toastService = inject(ToastService);
  private readonly syncService = inject(SyncService);

  protected readonly pendingInvitations = toSignal(
    this.vaultOrganizationUserNotificationsService.pendingInvitations$,
    { initialValue: [] },
  );
  protected readonly acceptingInvitationId = signal<string | null>(null);

  protected async acceptInvitation(invitation: PendingOrganizationInvitation) {
    this.acceptingInvitationId.set(invitation.organizationUserId);
    try {
      const accepted = await this.vaultOrganizationUserNotificationsService.acceptPendingInvitation(
        invitation,
        this.router.url,
      );
      if (accepted) {
        await this.syncService.fullSync(true);
        this.toastService.showToast({
          message: this.i18nService.t("invitationAcceptedDesc"),
          variant: "success",
          timeout: 10000,
        });
      }
    } catch {
      this.toastService.showToast({
        message: this.i18nService.t("inviteAcceptFailed"),
        variant: "error",
        timeout: 10000,
      });
    } finally {
      this.acceptingInvitationId.set(null);
    }
  }
}
